// The chess a board needs, without the board.
//
// Two boards exist today — the one you practise on and the small one on /lines — and each wrote its
// own version of "what can move where" and "play these moves from here". The tools coming next need
// several more, so the rules live here, once, where they can be tested without a DOM.

import { Chess } from "#vendor/chessjs/chess.js";
import type { Move } from "#vendor/chessjs/chess.js";

export type Side = "white" | "black";

/** Where every piece of the side to move can go: what Chessground calls `dests`. */
export function destsOf(chess: Chess): Map<string, string[]> {
    const dests = new Map<string, string[]>();
    (chess.moves({ verbose: true }) as Move[]).forEach((move) => {
        const from = dests.get(move.from);
        if (from) {
            from.push(move.to);
        } else {
            dests.set(move.from, [move.to]);
        }
    });
    return dests;
}

export function turnColor(chess: Chess): Side {
    return chess.turn() === "w" ? "white" : "black";
}

/** The fullmove number of a FEN, which is where a line's move numbering starts. */
export function fullmoveOf(fen: string): number {
    const parsed = Number(fen.split(" ")[5]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

/**
 * The move a UCI string names in this position, or null when it is not legal here. chess.js throws
 * on a move it does not know, and a line with one bad move in it should stop there, not take the
 * page down with it.
 */
export function legalUci(chess: Chess, uci: string): Move | null {
    if (!uci || uci.length < 4) return null;
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const promotion = uci[4] || "q";
    return (chess.moves({ verbose: true }) as Move[])
        .find((move) => move.from === from && move.to === to
            && (!move.promotion || move.promotion === promotion)) || null;
}

/** The same for SAN, which is how a history is replayed. */
export function legalSan(chess: Chess, san: string): boolean {
    return !!san && (chess.moves() as string[]).includes(san);
}

/**
 * Plays the UCI moves of a line from the current position, stopping at the first one that does not
 * fit. Answers how many were played, so the caller can tell a line that did not fit from one that did.
 */
export function playUciMoves(chess: Chess, uciMoves: string | null | undefined): number {
    const moves = (uciMoves || "").trim().split(/\s+/).filter(Boolean);
    let played = 0;
    for (const uci of moves) {
        const legal = legalUci(chess, uci);
        if (!legal) break;
        chess.move(legal.san);
        played++;
    }
    return played;
}

/** Plays a history of SAN moves, stopping at the first one that does not fit. */
export function playSanMoves(chess: Chess, history: string[]): number {
    let played = 0;
    for (const san of history) {
        if (!legalSan(chess, san)) break;
        chess.move(san);
        played++;
    }
    return played;
}

/** "e2e4" for a move, promotion included. */
export function uciOf(move: { from: string; to: string; promotion?: string }): string {
    return move.from + move.to + (move.promotion || "");
}

/** Everything about the position that is data, for the store and for whoever paints it. */
export interface PositionState {
    fen: string;
    turn: Side;
    history: string[];
    gameOver: boolean;
}

export function positionState(chess: Chess): PositionState {
    return {
        fen: chess.fen(),
        turn: turnColor(chess),
        history: chess.history() as string[],
        gameOver: chess.isGameOver(),
    };
}
